import { useRef } from "react";
import { motion } from "framer-motion";
import { CalendarDays, Film, MapPin, Scissors, Sparkles, X } from "lucide-react";
import { Blue2Page, Part } from "../Page";
import { EASE, VIEW } from "../bits";
import { EVENT } from "../events/event";
import "./Event.css";

/* Stitches Night Out inside the blue-2 frame. The event keeps its own
   candlelit branding (as on /events/stitches-night-out, the event bar and the
   candlelit card), so this page darkens the frame's header itself (see
   Event.css) instead of using a palette tone. All copy, links and the ticket
   URL match the original page. */

const ADDRESS = "715 West 180 North, American Fork, UT 84003";

const FEATURES = [
  { icon: Scissors, title: "Bring your handwork", text: <>Pack your favorite hand project and enjoy an evening with fellow makers.</> },
  { icon: Sparkles, title: "Find your crafty people", text: <>Make new friends and join in the fun with a chance to win prizes.</> },
  { icon: Film, title: "Make it a movie night", text: <>Enjoy <em>{EVENT.film}</em> on the big screen at {EVENT.venue}.</> },
];

const SPONSORS = [
  { src: "/events/sponsors/baby-lock.svg", alt: "Baby Lock", w: 145 },
  { src: "/events/sponsors/cutterpillar.png", alt: "Cutterpillar", w: 190 },
  { src: "/events/sponsors/oliso.png", alt: "Oliso", w: 135 },
  { src: "/events/sponsors/seamstress-for-the-band.jpg", alt: "Seamstress for the Band Quilting Co.", w: 110 },
  { src: "/events/sponsors/quilt-101.png", alt: "Quilt 101", w: 170 },
  { src: "/events/sponsors/harmony.png", alt: "Harmony", w: 200 },
];

/* A few fixed specks of light, so the header twinkles without randomness. */
const STARS = [[8, 18], [22, 72], [41, 12], [57, 64], [68, 22], [83, 48], [93, 14], [35, 86], [76, 82]];

function Header({ openPoster }: { openPoster: () => void }) {
  return <div className="bev-head">
    {STARS.map(([x, y], i) => <i key={i} className="bev-star" style={{ left: `${x}%`, top: `${y}%`, animationDelay: `${i * 0.4}s` }} aria-hidden="true" />)}
    <div className="bev-head-copy">
      <motion.p className="bev-kicker" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>{EVENT.kicker}<span className="bev-kicker-name"> · {EVENT.title}</span></motion.p>
      <motion.h1 className="bev-title" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.7, ease: EASE }}>
        {EVENT.headline[0]} <i>{EVENT.film}</i> {EVENT.headline[1]}<span className="bev-glint" aria-hidden="true">✦</span>
      </motion.h1>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.6, ease: EASE }}>
        <p className="bev-desc">Make new friends, work on your hand crafts, win prizes, and enjoy <em>{EVENT.film}!</em></p>
        <div className="bev-logistics">
          <p className="bev-when"><CalendarDays size={22} aria-hidden="true" /> October 20 · {EVENT.time}</p>
          <div className="bev-where">
            <MapPin size={20} aria-hidden="true" />
            <div>
              <p className="bev-venue">{EVENT.venue}</p>
              <p className="bev-address">{ADDRESS}</p>
            </div>
          </div>
        </div>
        <p className="bev-note" role="status">This event has ended. Thank you for joining us! Ticket sales are closed.</p>
      </motion.div>
    </div>
    <motion.button type="button" onClick={openPoster} className="bev-poster" aria-label="Expand Practical Magic 2 movie poster" aria-haspopup="dialog" aria-controls="bev-poster-modal"
      initial={{ opacity: 0, y: 26, rotate: 0 }} animate={{ opacity: 1, y: 0, rotate: 2 }} transition={{ delay: 0.3, duration: 0.8, ease: EASE }}>
      <img src={EVENT.poster} alt={EVENT.posterAlt} width={1080} height={1350} />
      <span className="bev-poster-hint">tap to see the full poster</span>
    </motion.button>
  </div>;
}

export function Blue2Event() {
  const dialog = useRef<HTMLDialogElement>(null);
  return <Blue2Page header={<Header openPoster={() => dialog.current?.showModal()} />}>
    <span className="bev-root" hidden />

    <dialog ref={dialog} id="bev-poster-modal" className="bev-modal" aria-label="Practical Magic 2 movie poster"
      onClick={e => { if (e.target === e.currentTarget) dialog.current?.close(); }}>
      <button type="button" className="bev-modal-close" aria-label="Close poster" onClick={() => dialog.current?.close()}><X size={24} aria-hidden="true" /></button>
      <img src={EVENT.poster} alt="Full Practical Magic 2 movie poster featuring Sandra Bullock and Nicole Kidman" width={1080} height={1350} />
    </dialog>

    <section className="bev-details" aria-labelledby="bev-details-title">
      <div className="b2-wrap">
        <motion.h2 id="bev-details-title" className="bev-h2" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={VIEW} transition={{ duration: 0.6, ease: EASE }}>
          Your project. Your people. <span>A little magic.</span>
        </motion.h2>
        <ul className="bev-features">
          {FEATURES.map((f, i) => <motion.li key={f.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={VIEW} transition={{ delay: i * 0.1, duration: 0.6, ease: EASE }}>
            <span className="bev-feature-icon"><f.icon size={22} aria-hidden="true" /></span>
            <h3>{f.title}</h3>
            <p>{f.text}</p>
          </motion.li>)}
        </ul>
      </div>
    </section>

    <Part p="faves">
      <section className="bev-thanks" aria-labelledby="bev-hosts-title">
        <div className="b2-wrap">
          <div className="bev-hosts">
            <p className="b2-note bev-hosts-note">hosted by</p>
            <h2 id="bev-hosts-title" className="bev-hosts-names">
              <a href="https://www.instagram.com/chelsea_b_porter/" target="_blank" rel="noreferrer">Chelsea B. Porter</a>
              <span aria-hidden="true">&amp;</span>
              <a href="https://www.instagram.com/iguessimakethings/" target="_blank" rel="noreferrer">@iguessimakethings</a>
            </h2>
          </div>
          <div className="bev-sponsors">
            <p className="bev-sponsors-title">With thanks to our sponsors</p>
            <ul>{SPONSORS.map(s => <li key={s.src}><img src={s.src} alt={s.alt} style={{ width: s.w }} /></li>)}</ul>
          </div>
        </div>
      </section>
    </Part>


  </Blue2Page>;
}
